import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Phone, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { tecnalData } from '../../data/tecnal';
import './ReferenciaPage.css';

const ReferenciaPage = () => {
  const { referencia } = useParams();
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const dataKeyMap = {
    'equipos-rotativos': 'referencias-equipos-rotativos',
    'expanders': 'referencias-expanders',
    'enfriadoras': 'referencias-enfriadoras'
  };

  const dataKey = dataKeyMap[referencia];
  const data = tecnalData[dataKey];

  const images = data ? (data.images || []) : [];
  const basePath = '/assets/tecnal/referencias';

  const openLightbox = (index) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };

  // Keyboard navigation for lightbox (must be before early return)
  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      if (e.key === 'ArrowRight') setSelectedImage(prev => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setSelectedImage(prev => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isLightboxOpen, images.length]);

  if (!data) {
    return (
      <div className="not-found-page" style={{ padding: '4rem', textAlign: 'center' }}>
        <h1>Referencia no encontrada</h1>
        <p>La referencia que busca no está disponible.</p>
      </div>
    );
  }

  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=Hola, me interesa información sobre ${data.title}`;

  const hasImages = images.length > 0;

  return (
    <div className="referencia-page" data-testid="referencia-page">
      {/* Hero Section */}
      <section className="referencia-hero">
        <div className="referencia-hero-content">
          <span className="referencia-parent-title">{data.parentTitle}</span>
          <h1 className="referencia-hero-title" data-testid="referencia-title">{data.title}</h1>
          {data.intro && data.intro.map((text, idx) => (
            <p key={idx} className="referencia-hero-description">{text}</p>
          ))}
        </div>
      </section>

      {/* Gallery Section - Masonry Grid */}
      {hasImages ? (
        <section className="referencia-gallery-section">
          <div className="container">
            <h2 className="section-title">Galería de instalaciones</h2>

            <div className="masonry-grid" data-testid="referencia-thumbnails">
              {images.map((img, index) => (
                <div
                  key={index}
                  className="masonry-item"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={`${basePath}/${img}`}
                    alt={`${data.title} - Imagen ${index + 1}`}
                    className="masonry-image"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/400x300?text=${encodeURIComponent(data.title)}`;
                    }}
                    data-testid={index === 0 ? 'referencia-main-image' : undefined}
                  />
                  <div className="masonry-overlay">
                    <ZoomIn size={26} strokeWidth={1.5} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="referencia-gallery-section">
          <div className="container">
            <div className="no-images-placeholder">
              <h3>Galería de instalaciones</h3>
              <p>Imágenes de nuestras instalaciones próximamente disponibles.</p>
              <p>Contactenos para obtener más información sobre nuestras referencias en Argentina.</p>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="referencia-cta-section">
        <div className="container">
          <h2 className="referencia-cta-title">
            ¿Desea conocer más sobre nuestras instalaciones?
          </h2>
          <p className="referencia-cta-subtitle">
            Contactanos para visitar nuestras referencias o solicitar información técnica
          </p>
          <Button
            size="lg"
            className="referencia-cta-button"
            onClick={() => window.open(whatsappLink, '_blank')}
            data-testid="referencia-contact-button"
          >
            <Phone className="mr-2" size={20} />
            Contactar
          </Button>
        </div>
      </section>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={() => setIsLightboxOpen(false)} data-testid="lightbox">
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="lightbox-close"
              onClick={() => setIsLightboxOpen(false)}
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>

            <div className="lightbox-image-area">
              {images.length > 1 && (
                <button
                  className="lightbox-nav prev"
                  onClick={() => setSelectedImage(prev => (prev - 1 + images.length) % images.length)}
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft size={28} />
                </button>
              )}
              <img
                key={`lb-${selectedImage}`}
                src={`${basePath}/${images[selectedImage]}`}
                alt={`${data.title} - Imagen ${selectedImage + 1}`}
                className="lightbox-image"
              />
              {images.length > 1 && (
                <button
                  className="lightbox-nav next"
                  onClick={() => setSelectedImage(prev => (prev + 1) % images.length)}
                  aria-label="Siguiente imagen"
                >
                  <ChevronRight size={28} />
                </button>
              )}
            </div>

            <div className="lightbox-footer">
              <span className="lightbox-counter">{selectedImage + 1} / {images.length}</span>
              {images.length > 1 && (
                <div className="lightbox-thumbs">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      className={`lightbox-thumb${index === selectedImage ? ' active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                      aria-label={`Ver imagen ${index + 1}`}
                    >
                      <img
                        src={`${basePath}/${img}`}
                        alt=""
                        className="lightbox-thumb-img"
                        onError={(e) => { e.target.src = `https://via.placeholder.com/80x60?text=${index + 1}`; }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferenciaPage;
